import React, { Fragment } from "react";
import Page from "../../../../components/page";
import Image from "@vorne/gatsby-theme-marketing/components/image/image";
import { graphql } from "gatsby";
import PageTitle from "@vorne/gatsby-theme-marketing/components/pagetitle/pageTitle";
import QuickLinks from "@vorne/gatsby-theme-marketing/components/quicklinks/quickLinks";
import {
  Heading1,
  Heading2,
  Body1,
} from "@vorne/gatsby-theme-marketing/components/typography/typography";
import Link from "@vorne/gatsby-theme-marketing/components/link/link";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
} from "@vorne/gatsby-theme-marketing/components/table/table";
import Routes from "../../../../routes";
import CallToActionButton from "@vorne/gatsby-theme-marketing/components/button/calltoaction/callToAction";
import Section from "@vorne/gatsby-theme-marketing/components/section/section";
import Center from "@vorne/gatsby-theme-marketing/components/layout/center/center";
import { Seo } from "@vorne/gatsby-theme-marketing/components/seo/v2";

const XL800 = ({ data }) => {
  return (
    <Page>
      <Section>
        <PageTitle>XL800 Productivity Appliance™</PageTitle>
        <QuickLinks tag="h2" />
        <Heading1 id="overview">Overview</Heading1>
        <Body1>
          The XL800 features a visually expressive graphics display that embeds
          real-time performance data into a virtually unlimited number of
          screens – instantly responding to plant floor events as they occur.
          Like all XL products it is a complete platform for production
          monitoring and performance management. In fact – the only difference
          between XL models is the type of plant floor visual display.
        </Body1>
        <Body1>
          This page includes information specific to the XL800: how to order,
          pricing, and specifications. For a detailed description of features
          (e.g. production monitoring, performance management, integrated data
          historian, analytics, browser interface, etc.) please refer to the{" "}
          <Link url={Routes.Vorne.XL.XL}>main XL page</Link>.
        </Body1>
        <Center $horizontally>
          <Image
            src={data.vorneXl800Models}
            alt="Image of XL 800 displays."
            renderCaption={() => (
              <Fragment>
                XL models top to bottom: XL800-32080T (80 Columns, Tricolor
                LED), XL800-32160T (160 Columns, Tricolor LED)
              </Fragment>
            )}
          />
        </Center>
        <Center $horizontally>
          <CallToActionButton url={Routes.Vorne.XL.FreeTrial}>
            TRY XL FOR 90 DAYS
          </CallToActionButton>
        </Center>
        <Heading1 id="howToOrder">How to Order</Heading1>
        <Body1>
          <Link url={Routes.Vorne.XL.FreeTrial}>Request a free trial</Link> or
          contact us at +1.630.875.3600
        </Body1>
        <Heading1 id="pricing">Pricing</Heading1>
        <Heading2>XL800 Models</Heading2>
        <Body1>
          The following prices are for single unit quantities. Volume discounts
          are available for purchases of ten (10) or more units.
        </Body1>
        <Body1>XL800-ABC Example: XL800-32080T</Body1>
        <Table align="L L L">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Key</TableHeaderCell>
              <TableHeaderCell>Description</TableHeaderCell>
              <TableHeaderCell>Choices</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>A</TableCell>
              <TableCell>Display Rows</TableCell>
              <TableCell>32 = 32 Rows</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>B</TableCell>
              <TableCell>Display Columns</TableCell>
              <TableCell>
                <Body1>080 = 80 Columns</Body1>
                <Body1>160 = 160 Columns</Body1>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>C</TableCell>
              <TableCell>Display Color</TableCell>
              <TableCell>T = Tricolor LED</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Table align="L L R">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Model Number</TableHeaderCell>
              <TableHeaderCell>Description</TableHeaderCell>
              <TableHeaderCell>Price</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>XL800-32080T</TableCell>
              <TableCell>32 x 80 Pixel, Tricolor LED Display</TableCell>
              <TableCell>$4,190</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>XL800-32160T</TableCell>
              <TableCell>32 x 160 Pixel, Tricolor LED Display</TableCell>
              <TableCell>$4,990</TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <Body1>
          Pricing shown is for North American customers in USD. Contact us for
          international pricing.
        </Body1>
        <Heading2>XL800 Options</Heading2>
        <Body1>
          The following is a list of available options for the XL800 lean
          manufacturing tool. All XL barcode kits include a barcode scanner, 25′
          cable, and power supply.
        </Body1>
        <Table align="L L R">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Model Number</TableHeaderCell>
              <TableHeaderCell>Description</TableHeaderCell>
              <TableHeaderCell>Price</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>EXP-1</TableCell>
              <TableCell>16 Input Expansion Inputs</TableCell>
              <TableCell>$349</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>EXP-2</TableCell>
              <TableCell>6 Relay Expansion Outputs</TableCell>
              <TableCell>$249</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>93-0184</TableCell>
              <TableCell>
                25′ XL Barcode Interface Cable (included with kit)
              </TableCell>
              <TableCell>$28</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Link
                  url={Routes.Vorne.XLBarcodeKits + "#LightIndustrial930197"}
                >
                  93-0197
                </Link>
              </TableCell>
              <TableCell>2D Light Industrial Barcode Kit</TableCell>
              <TableCell>$298</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Link
                  url={
                    Routes.Vorne.XLBarcodeKits +
                    "#LightIndustrialWireless930227"
                  }
                >
                  93-0227
                </Link>
              </TableCell>
              <TableCell>2D Light Industrial Wireless Barcode Kit</TableCell>
              <TableCell>$698</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Link url={Routes.Vorne.XLBarcodeKits + "#Industrial930228"}>
                  93-0228
                </Link>
              </TableCell>
              <TableCell>2D Industrial Barcode Kit</TableCell>
              <TableCell>$498</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Link
                  url={Routes.Vorne.XLBarcodeKits + "#IndustrialWireless930245"}
                >
                  93-0245
                </Link>
              </TableCell>
              <TableCell>2D Industrial Wireless Barcode Kit</TableCell>
              <TableCell>$998</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>SVC-001</TableCell>
              <TableCell>
                Professional Services (1-Hour Block of Reserved Engineering
                Time)
              </TableCell>
              <TableCell>$150</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Body1>
          Pricing shown is for North American customers in USD. Contact us for
          international pricing.
        </Body1>
        <Heading1 id="specifications">Specifications</Heading1>
        <Heading2>Visual Display</Heading2>
        <Table align="L L">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Item</TableHeaderCell>
              <TableHeaderCell>Specification</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>Display Rows</TableCell>
              <TableCell>32 Rows</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Display Columns</TableCell>
              <TableCell>
                80 or 160 Columns (See{" "}
                <Link url="#howToOrder">How to Order</Link>)
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Display Color</TableCell>
              <TableCell>Tricolor LED</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Heading2>I/O</Heading2>
        <Table align="L L">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Item</TableHeaderCell>
              <TableHeaderCell>Specification</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>8 Standard Inputs</TableCell>
              <TableCell>
                <Body1>5 to 24 VDC (Sinking or Sourcing)</Body1>
                <Body1>Up to 4,000 Hz</Body1>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>16 Expansion Inputs</TableCell>
              <TableCell>
                <Body1>5 to 24 VDC (Sinking or Sourcing)</Body1>
                <Body1>Up to 10 Hz</Body1>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>1 Standard Output</TableCell>
              <TableCell>
                <Body1>250 VAC, 1 A Resistive Load</Body1>
                <Body1>30 VDC, 1 A Resistive Load</Body1>
                <Body1>SPDT Relay</Body1>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>6 Expansion Outputs</TableCell>
              <TableCell>
                <Body1>250 VAC, 1 A Resistive Load</Body1>
                <Body1>30 VDC, 1 A Resistive Load</Body1>
                <Body1>1 Output SPDT Relay, 5 Outputs SPST Relay</Body1>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Heading2>Communication Ports</Heading2>
        <Table align="L L">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Item</TableHeaderCell>
              <TableHeaderCell>Specification</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>Ethernet Port</TableCell>
              <TableCell>
                <Body1>10/100Base-TX</Body1>
                <Body1>Automatic Crossover Detection</Body1>
                <Body1>Full- or Half-Duplex</Body1>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Serial Port 1</TableCell>
              <TableCell>
                <Body1>RS-232</Body1>
                <Body1>300 to 115,200 bps Baud Rate</Body1>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Serial Port 2</TableCell>
              <TableCell>
                <Body1>RS-232 or RS-422/RS-485 (DIP Switch Selectable)</Body1>
                <Body1>300 to 115,200 bps Baud Rate</Body1>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Heading2>Operating Conditions</Heading2>
        <Table align="L L">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Item</TableHeaderCell>
              <TableHeaderCell>Specification</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>Power Requirements</TableCell>
              <TableCell>
                <Body1>Voltage: 100 to 240 VAC, 50 to 60 Hz</Body1>
                <Body1>Current: 2.0 or 3.3 A (Depending on Model)</Body1>
                <Body1>Power: 130 or 230 Watts (Depending on Model)</Body1>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Operating Temperature</TableCell>
              <TableCell>32°F to 122°F (0°C to 50°C) Ambient</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Operating Humidity</TableCell>
              <TableCell>
                5% to 95% Relative Humidity (Non-Condensing){" "}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Heading2>Dimensions and Weight</Heading2>
        <Body1>
          The following are the dimensions and weight for the XL800 lean
          manufacturing tool. The dimensions shown represent the height, width,
          and depth (H x W x D) of the product. The same 13.7″ (35 cm) height
          and 3.5″ (9 cm) depth dimensions are shared by all XL800 models.
        </Body1>
        <Table align="L L">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Model Number</TableHeaderCell>
              <TableHeaderCell>Dimensions</TableHeaderCell>
              <TableHeaderCell>Weight</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>XL800-32080T</TableCell>
              <TableCell>13.7″ x 26.2″ x 3.5″ (35 cm x 67 cm x 9 cm)</TableCell>
              <TableCell>19 lb (9 kg)</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>XL800-32160T</TableCell>
              <TableCell>
                13.7″ x 50.2″ x 3.5″ (35 cm x 128 cm x 9 cm)
              </TableCell>
              <TableCell>33 lb (15 kg)</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Center $horizontally>
          <CallToActionButton url={Routes.Vorne.XL.FreeTrial}>
            TRY XL FOR 90 DAYS
          </CallToActionButton>
        </Center>
      </Section>
    </Page>
  );
};

export default XL800;

export function Head() {
  return (
    <Seo
      title="XL800 Productivity Appliance™"
      description="Learn about features, pricing, and specs for the XL800 Productivity Appliance. Get started today with a free 90-day trial."
    />
  );
}

export const query = graphql`
  query {
    vorneXl800Models: file(relativePath: { eq: "vorne-xl800-models.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
  }
`;
